import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

// PrimeNG Modules
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';
import { AccordionModule } from 'primeng/accordion';
import { DividerModule } from 'primeng/divider';
import { ChipModule } from 'primeng/chip';

// Services
import { UserService } from '../../layout/service/user.service';
import { MessageService } from 'primeng/api';

interface AnatomyHistory {
  anatomy_answer_id: number;
  anatomy_id: number;
  anatomy_name: string;
  completion_date: string;
  total_questions: number;
  average_score: number;
  created_at: string;
}

interface AnatomyAnswerDetail {
  question_number: number;
  question_text: string;
  user_answer: string;
  correct_answer: string;
  score: number;
  explanation?: string;
  image_url?: string;
  anatomy_part?: string; // Add this for anatomy-specific data
}

interface AnatomyHistoryDetail {
  anatomy_answer_id: number;
  anatomy_name: string;
  completion_date: string;
  total_questions: number;
  average_score: number;
  answers: AnatomyAnswerDetail[];
}

@Component({
  selector: 'app-history-anatomy',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    TagModule,
    SkeletonModule,
    DialogModule,
    ToastModule,
    ProgressBarModule,
    AccordionModule,
    DividerModule,
    ChipModule
  ],
  providers: [MessageService],
  template: `
    <div class="min-h-screen bg-gray-50 p-6">
      <div class="max-w-6xl mx-auto">
        <!-- Header -->
        <div class="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div class="flex justify-between items-center">
            <div>
              <h1 class="text-3xl font-bold text-gray-900">Riwayat Pembelajaran Anatomi</h1>
              <p class="text-gray-600 mt-2">Lihat progress dan hasil pembelajaran anatomi Anda</p>
            </div>
            <div class="flex gap-3">
              <div class="bg-green-50 px-4 py-2 rounded-lg">
                <div class="text-sm text-green-600 font-medium">{{historyList.length}} Sesi</div>
              </div>
              <p-button
                icon="pi pi-arrow-left"
                label="Kembali"
                severity="secondary"
                (onClick)="goBack()"
              ></p-button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div *ngIf="loading" class="space-y-6">
          <div *ngFor="let item of [1,2,3,4]" class="bg-white rounded-xl shadow-sm border p-6 animate-pulse">
            <div class="flex justify-between items-start mb-4">
              <div class="flex-1">
                <div class="bg-gray-200 rounded h-6 w-3/4 mb-2"></div>
                <div class="bg-gray-200 rounded h-4 w-1/2"></div>
              </div>
              <div class="bg-gray-200 rounded h-8 w-20"></div>
            </div>
            <div class="flex gap-4 mb-4">
              <div class="bg-gray-200 rounded h-4 w-24"></div>
              <div class="bg-gray-200 rounded h-4 w-24"></div>
              <div class="bg-gray-200 rounded h-4 w-24"></div>
            </div>
            <div class="bg-gray-200 rounded h-10 w-full"></div>
          </div>
        </div>

        <!-- History List -->
        <div *ngIf="!loading && historyList.length > 0" class="space-y-6">
          <div *ngFor="let history of historyList; let i = index"
               class="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow duration-300 p-6">

            <!-- History Header -->
            <div class="flex justify-between items-start mb-4">
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-gray-900 mb-2">
                  {{history.anatomy_name}}
                </h3>
                <div class="flex items-center gap-2 text-sm text-gray-500">
                  <i class="pi pi-calendar"></i>
                  <span>{{formatDate(history.completion_date)}}</span>
                </div>
              </div>
              <div class="text-right">
                <div class="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium mb-2">
                  Rata-rata: {{history.average_score.toFixed(1)}}%
                </div>
                <div [ngClass]="getScoreColor(history.average_score)"
                     class="px-2 py-1 rounded text-xs font-medium">
                  {{getScoreLabel(history.average_score)}}
                </div>
              </div>
            </div>

            <!-- Stats -->
            <div class="grid grid-cols-3 gap-4 mb-4">
              <div class="text-center p-3 bg-gray-50 rounded-lg">
                <div class="text-lg font-semibold text-gray-900">{{history.total_questions}}</div>
                <div class="text-sm text-gray-600">Total Soal</div>
              </div>
              <div class="text-center p-3 bg-blue-50 rounded-lg">
                <div class="text-lg font-semibold text-blue-600">{{history.average_score.toFixed(1)}}%</div>
                <div class="text-sm text-blue-600">Skor Rata-rata</div>
              </div>
              <div class="text-center p-3 bg-green-50 rounded-lg">
                <div class="text-lg font-semibold text-green-600">
                  {{getCorrectAnswersCount(history.average_score, history.total_questions)}}/{{history.total_questions}}
                </div>
                <div class="text-sm text-green-600">Benar</div>
              </div>
            </div>

            <!-- Progress Bar -->
            <div class="mb-4">
              <div class="flex justify-between items-center mb-2">
                <span class="text-sm font-medium text-gray-700">Progress Keseluruhan</span>
                <span class="text-sm text-gray-500">{{history.average_score.toFixed(1)}}%</span>
              </div>
              <p-progressBar
                [value]="history.average_score"
                [style]="{'height': '8px'}"
                [showValue]="false"
              ></p-progressBar>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-3">
              <p-button
                label="Lihat Detail"
                icon="pi pi-eye"
                severity="primary"
                size="small"
                (onClick)="viewDetails(history)"
                [loading]="loadingDetail && selectedHistoryId === history.anatomy_answer_id"
              ></p-button>
              <p-button
                label="Pelajari Lagi"
                icon="pi pi-refresh"
                severity="secondary"
                size="small"
                (onClick)="retakeAnatomy(history)"
              ></p-button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="!loading && historyList.length === 0" class="text-center py-20">
          <div class="bg-white rounded-xl shadow-sm border p-12 max-w-lg mx-auto">
            <i class="pi pi-history text-6xl text-gray-300 mb-6 block"></i>
            <h3 class="text-xl font-semibold text-gray-700 mb-3">
              Belum ada riwayat pembelajaran
            </h3>
            <p class="text-gray-500 mb-6">
              Mulai belajar anatomi untuk melihat riwayat progress Anda di sini
            </p>
            <p-button
              label="Mulai Belajar"
              icon="pi pi-play"
              severity="primary"
              (onClick)="goToAnatomy()"
            ></p-button>
          </div>
        </div>
      </div>

      <!-- Detail Dialog -->
      <p-dialog
        header="Detail Jawaban"
        [(visible)]="showDetailDialog"
        [modal]="true"
        [style]="{width: '90vw', maxWidth: '1000px'}"
        [closable]="true"
        [draggable]="false"
        [resizable]="false"
      >
        <div *ngIf="selectedDetail" class="space-y-6">
          <!-- Detail Header -->
          <div class="border-b pb-4">
            <h3 class="text-xl font-semibold text-gray-900 mb-2">
              {{selectedDetail.anatomy_name}}
            </h3>
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-4 text-sm text-gray-600">
                <span><i class="pi pi-calendar mr-1"></i>{{formatDate(selectedDetail.completion_date)}}</span>
                <span><i class="pi pi-book mr-1"></i>{{selectedDetail.total_questions}} Soal</span>
              </div>
              <div class="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                Rata-rata: {{selectedDetail.average_score.toFixed(1)}}%
              </div>
            </div>
          </div>

          <!-- Summary Stats -->
          <div class="grid grid-cols-4 gap-4 mb-6">
            <div class="text-center p-3 bg-gray-50 rounded-lg">
              <div class="text-lg font-semibold text-gray-900">{{selectedDetail.total_questions}}</div>
              <div class="text-sm text-gray-600">Total Soal</div>
            </div>
            <div class="text-center p-3 bg-blue-50 rounded-lg">
              <div class="text-lg font-semibold text-blue-600">{{selectedDetail.average_score.toFixed(1)}}%</div>
              <div class="text-sm text-blue-600">Rata-rata</div>
            </div>
            <div class="text-center p-3 bg-green-50 rounded-lg">
              <div class="text-lg font-semibold text-green-600">{{getCorrectCount(selectedDetail.answers)}}</div>
              <div class="text-sm text-green-600">Benar</div>
            </div>
            <div class="text-center p-3 bg-red-50 rounded-lg">
              <div class="text-lg font-semibold text-red-600">{{getIncorrectCount(selectedDetail.answers)}}</div>
              <div class="text-sm text-red-600">Salah</div>
            </div>
          </div>

          <!-- Answers Detail -->
          <div class="space-y-4 max-h-96 overflow-y-auto">
            <h4 class="text-lg font-semibold text-gray-900 mb-3">Detail Jawaban</h4>
            <p-accordion>
              <p-accordionTab
                *ngFor="let answer of selectedDetail.answers; let i = index"
                [header]="'Bagian ' + answer.question_number + ': ' + (answer.anatomy_part || 'Tidak diidentifikasi') + ' - ' + (answer.score === 100 ? 'Benar' : 'Salah') + ' (' + answer.score.toFixed(1) + '%)'">

                <div class="space-y-4">
                  <!-- Question -->
                  <div>
                    <h5 class="font-medium text-gray-900 mb-2">Tugas:</h5>
                    <div class="text-gray-700 leading-relaxed" [innerHTML]="answer.question_text"></div>
                  </div>

                  <!-- Image if available -->
                  <div *ngIf="getImageSrc(answer.image_url)" class="text-center">
                    <img [src]="getImageSrc(answer.image_url)"
                         alt="Gambar soal {{answer.question_number}}"
                         class="max-w-full h-auto rounded-lg border shadow-sm mx-auto"
                         style="max-height: 300px;">
                  </div>

                  <!-- Answer Display -->
                  <div class="grid grid-cols-1 gap-4">
                    <!-- User's Identification -->
                    <div class="border rounded-lg p-4"
                         [ngClass]="answer.score === 100 ? 'border-green-200 bg-green-50' : 'border-blue-200 bg-blue-50'">
                      <h6 class="font-medium mb-2"
                          [ngClass]="answer.score === 100 ? 'text-green-800' : 'text-blue-800'">
                        Identifikasi Anda:
                      </h6>
                      <p class="text-gray-700 font-medium">{{answer.user_answer || 'Tidak dijawab'}}</p>
                      <div class="mt-2 text-sm"
                           [ngClass]="answer.score === 100 ? 'text-green-600' : 'text-blue-600'">
                        Skor: {{answer.score.toFixed(1)}}%
                      </div>
                    </div>
                  </div>

                  <!-- Function/Explanation -->
                  <div *ngIf="answer.explanation" class="border-t pt-4">
                    <h6 class="font-medium text-gray-900 mb-2">Fungsi & Penjelasan:</h6>
                    <div class="prose prose-sm max-w-none">
                      <p class="text-gray-700 leading-relaxed">{{answer.explanation}}</p>
                    </div>
                  </div>
                </div>
              </p-accordionTab>
            </p-accordion>
          </div>
        </div>

        <div *ngIf="loadingDetail" class="text-center py-8">
          <i class="pi pi-spinner pi-spin text-2xl text-blue-500 mb-3 block"></i>
          <p class="text-gray-600">Memuat detail jawaban...</p>
        </div>
      </p-dialog>

      <!-- Toast Messages -->
      <p-toast></p-toast>
    </div>
  `,
  styles: [`
    .line-clamp-2 {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .line-clamp-3 {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    :host ::ng-deep .p-dialog-header {
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
    }

    :host ::ng-deep .p-accordion .p-accordion-tab .p-accordion-header {
      border-radius: 0.5rem;
      margin-bottom: 0.5rem;
    }

    :host ::ng-deep .p-accordion .p-accordion-tab .p-accordion-content {
      border-radius: 0 0 0.5rem 0.5rem;
    }

    :host ::ng-deep .p-progressbar {
      border-radius: 1rem;
      overflow: hidden;
    }

    :host ::ng-deep .p-progressbar .p-progressbar-value {
      border-radius: 1rem;
    }
  `]
})
export class HistoryAnatomy implements OnInit {
  historyList: AnatomyHistory[] = [];
  selectedDetail: AnatomyHistoryDetail | null = null;
  loading = false;
  loadingDetail = false;
  showDetailDialog = false;
  selectedHistoryId: number | null = null;

  constructor(
    private userService: UserService,
    private messageService: MessageService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadHistory();
  }

  async loadHistory() {
    this.loading = true;
    try {
      const userData = localStorage.getItem('user');
      if (!userData) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Data pengguna tidak ditemukan. Silakan login kembali.'
        });
        this.router.navigate(['/auth/login']);
        return;
      }

      const user = JSON.parse(userData);

      // First, get all anatomy data to create a mapping
      const anatomyResponse = await this.userService.getAnatomy({});
      let anatomyMap: { [key: string]: any } = {};

      if (anatomyResponse && anatomyResponse.status) {
        // Create a map of anatomy_id to anatomy data
        (anatomyResponse.data || []).forEach((anatomy: any) => {
          anatomyMap[anatomy.id || anatomy.anatomy_id] = anatomy;
        });
      } else {
        // Failed to load anatomy data
      }

      // Get user's anatomy answers
      const response = await this.userService.getAnatomyAnswers({ user_id: user.ID_USER });

      if (response && response.status) {
        // Transform the response data to match our interface
        const historyItems = response.data || [];

        // Create promises to get anatomy details for each item
        const enrichedHistoryPromises = historyItems.map(async (item: any) => {
          try {
            const anatomyId = item.anatomy_id;
            const anatomyInfo = anatomyMap[anatomyId];

            // Get anatomy details to get question count and more info
            const anatomyDetails = await this.userService.getAnatomyDetails({ anatomy_id: anatomyId });

            let anatomyName = `Anatomi ${anatomyId}`;
            let totalQuestions = 1;

            if (anatomyInfo) {
              anatomyName = anatomyInfo.anatomy_name || anatomyInfo.name || anatomyName;
            }

            if (anatomyDetails && anatomyDetails.status) {
              totalQuestions = anatomyDetails.data?.length || (anatomyDetails.data?.details_count || 1);
              // If we have better name from details, use it
              if (anatomyDetails.data?.anatomy_name) {
                anatomyName = anatomyDetails.data.anatomy_name;
              }
            }

            return {
              anatomy_answer_id: parseInt(item.id),
              anatomy_id: parseInt(anatomyId),
              anatomy_name: anatomyName,
              completion_date: item.created_at,
              total_questions: totalQuestions,
              average_score: parseFloat(item.skor) || 0,
              created_at: item.created_at
            };
          } catch (error) {
            // If we can't get anatomy details, use default values
            const anatomyInfo = anatomyMap[item.anatomy_id];
            const anatomyName = anatomyInfo?.anatomy_name || anatomyInfo?.name || `Anatomi ${item.anatomy_id}`;

            return {
              anatomy_answer_id: parseInt(item.id),
              anatomy_id: parseInt(item.anatomy_id),
              anatomy_name: anatomyName,
              completion_date: item.created_at,
              total_questions: 1,
              average_score: parseFloat(item.skor) || 0,
              created_at: item.created_at
            };
          }
        });

        // Wait for all anatomy details to be fetched
        this.historyList = await Promise.all(enrichedHistoryPromises);
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: response?.message || 'Gagal memuat riwayat pembelajaran'
        });
      }
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Terjadi kesalahan saat memuat riwayat pembelajaran'
      });
    } finally {
      this.loading = false;
    }
  }

  async viewDetails(history: AnatomyHistory) {
    this.selectedHistoryId = history.anatomy_answer_id;
    this.loadingDetail = true;
    this.showDetailDialog = true;

    try {
      // Get the actual anatomy questions first
      const anatomyDetailsResponse = await this.userService.getAnatomyDetails({
        anatomy_id: history.anatomy_id
      });

      // Then get user's answers
      const response = await this.userService.getAnatomyAnswerDetails({
        anatomy_answer_id: history.anatomy_answer_id
      });

      if (response && response.status) {
        // Create a map of anatomy_detail_id to question info
        let questionsMap: { [key: string]: any } = {};
        if (anatomyDetailsResponse && anatomyDetailsResponse.status) {
          (anatomyDetailsResponse.data || []).forEach((question: any, index: number) => {
            questionsMap[question.id || question.anatomy_detail_id] = {
              question_text: question.question || question.description || `Identifikasi bagian anatomi ${index + 1}`,
              image_url: question.image_url || question.image || null,
              correct_answer: question.answer || question.correct_answer || '',
              order: index + 1
            };
          });
        } else {
          console.warn('Failed to load anatomy questions:', anatomyDetailsResponse?.message);
        }

        // Transform the response data to match our interface
        const transformedAnswers = (response.data || []).map((item: any, index: number) => {
          const questionInfo = questionsMap[item.anatomy_detail_id] || {};

          return {
            question_number: questionInfo.order || (index + 1),
            question_text: questionInfo.question_text || `Identifikasi bagian anatomi ${index + 1}`,
            user_answer: item.answer || 'Tidak dijawab',
            correct_answer: questionInfo.correct_answer || item.answer || 'Tidak tersedia',
            score: parseFloat(item.score) || 0,
            explanation: item.function || '', // Function/explanation of the anatomy part
            image_url: questionInfo.image_url,
            anatomy_part: item.answer || '' // The anatomy part they identified
          };
        });

        this.selectedDetail = {
          anatomy_answer_id: history.anatomy_answer_id,
          anatomy_name: history.anatomy_name,
          completion_date: history.completion_date,
          total_questions: transformedAnswers.length,
          average_score: history.average_score,
          answers: transformedAnswers
        };
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: response?.message || 'Gagal memuat detail jawaban'
        });
        this.showDetailDialog = false;
      }
    } catch (error) {
      console.error('Error loading anatomy answer details:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Terjadi kesalahan saat memuat detail jawaban'
      });
      this.showDetailDialog = false;
    } finally {
      this.loadingDetail = false;
      this.selectedHistoryId = null;
    }
  }

  retakeAnatomy(history: AnatomyHistory) {
    // Navigate to anatomy details page for retaking
    this.router.navigate(['/app/osce/anatomi'], {
      queryParams: { retake: history.anatomy_id }
    });
  }

  goToAnatomy() {
    this.router.navigate(['/app/osce/anatomi']);
  }

  goBack() {
    this.router.navigate(['/app/osce/anatomi']);
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'Tanggal tidak tersedia';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  }

  getScoreColor(score: number): string {
    if (score >= 80) return 'bg-green-100 text-green-700';
    if (score >= 60) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  }

  getScoreLabel(score: number): string {
    if (score >= 80) return 'Sangat Baik';
    if (score >= 60) return 'Baik';
    if (score >= 40) return 'Cukup';
    return 'Perlu Perbaikan';
  }

  getCorrectAnswersCount(averageScore: number, totalQuestions: number): number {
    return Math.round((averageScore / 100) * totalQuestions);
  }

  getCorrectCount(answers: AnatomyAnswerDetail[]): number {
    return answers.filter(answer => answer.score === 100).length;
  }

  getIncorrectCount(answers: AnatomyAnswerDetail[]): number {
    return answers.filter(answer => answer.score !== 100).length;
  }

  getImageSrc(imageData: string | null): string | null {
    if (!imageData) return null;

    // If already a data URL, return as is
    if (imageData.startsWith('data:')) {
      return imageData;
    }

    // If it's a regular URL, return as is
    if (imageData.startsWith('http')) {
      return imageData;
    }

    // If it's base64 without data URL prefix, add it
    return `data:image/jpeg;base64,${imageData}`;
  }
}
